import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { OrderCard } from '../components/OrderCard'
import { OrderListSkeleton } from '../components/Skeleton'
import { AUTH_PATHS } from '../constants/auth'
import {
  ORDER_FILTER,
  ORDER_FILTER_OPTIONS,
  ORDER_FILTER_PARAMS,
  ORDER_UI,
  type OrderFilter,
} from '../constants/orders'
import {
  isOrderFilter,
  matchesOrderFilter,
  matchesOrderSearch,
  orderFilterCounts,
  orderListCountLabel,
} from '../helpers/orders'
import { useOrders } from '../hooks/useOrders'
import '../styles/orders-page.scss'

export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const rawStatus = searchParams.get(ORDER_FILTER_PARAMS.status)
  const activeFilter: OrderFilter = isOrderFilter(rawStatus) ? rawStatus : ORDER_FILTER.all
  const searchQuery = searchParams.get(ORDER_FILTER_PARAMS.query) ?? ''

  const statusParam =
    activeFilter === ORDER_FILTER.delivered
      ? 'delivered'
      : activeFilter === ORDER_FILTER.cancelled
        ? 'cancelled'
        : activeFilter === ORDER_FILTER.active
          ? 'pending'
          : undefined

  const { orders: allOrders } = useOrders({ status: 'all' })
  const { orders: activeTabOrders, loading, error, cancellingId, cancel } = useOrders({
    status: statusParam,
  })

  const counts = useMemo(() => orderFilterCounts(allOrders), [allOrders])

  const targetOrders = activeFilter === ORDER_FILTER.all ? allOrders : activeTabOrders

  const filteredOrders = useMemo(() => {
    return targetOrders.filter(
      (order) =>
        matchesOrderFilter(order, activeFilter) &&
        matchesOrderSearch(order, searchQuery),
    )
  }, [targetOrders, activeFilter, searchQuery])

  function updateFilter(nextStatus: OrderFilter, nextQuery: string) {
    const params = new URLSearchParams()
    if (nextStatus !== ORDER_FILTER.all) {
      params.set(ORDER_FILTER_PARAMS.status, nextStatus)
    }
    if (nextQuery.trim()) {
      params.set(ORDER_FILTER_PARAMS.query, nextQuery.trim())
    }
    setSearchParams(params, { replace: true })
  }

  function handleFilterSelect(status: OrderFilter) {
    updateFilter(status, searchQuery)
  }

  function handleSearchChange(query: string) {
    updateFilter(activeFilter, query)
  }

  function handleClearFilters() {
    setSearchParams(new URLSearchParams(), { replace: true })
  }

  const hasAnyOrders = allOrders.length > 0 || targetOrders.length > 0

  return (
    <div className="orders-page">
      <header className="orders-page__head">
        <div>
          <h1>{ORDER_UI.listTitle}</h1>
          <p className="orders-page__lead">{ORDER_UI.listLead}</p>
        </div>
        {hasAnyOrders ? (
          <p className="orders-page__count">
            {orderListCountLabel(filteredOrders.length, allOrders.length || targetOrders.length)}
          </p>
        ) : null}
      </header>

      {error ? <p className="orders-page__error">{error}</p> : null}

      {hasAnyOrders ? (
        <div className="orders-page__filters">
          <div className="orders-page__search">
            <Icon name="search" className="orders-page__search-icon" size={18} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={ORDER_UI.searchPlaceholder}
              aria-label={ORDER_UI.searchLabel}
              className="orders-page__search-input"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                className="orders-page__search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            ) : null}
          </div>

          <div className="orders-page__tabs" role="tablist" aria-label={ORDER_UI.filterLabel}>
            {ORDER_FILTER_OPTIONS.map((option) => {
              const isActive = activeFilter === option.id
              const count = counts[option.id]
              return (
                <button
                  key={option.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`orders-page__tab ${isActive ? 'is-active' : ''}`}
                  onClick={() => handleFilterSelect(option.id)}
                >
                  <span>{option.label}</span>
                  <span className="orders-page__tab-count">{count}</span>
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {loading && targetOrders.length === 0 ? (
        <OrderListSkeleton />
      ) : !hasAnyOrders ? (
        <div className="orders-page__empty">
          <p>{ORDER_UI.emptyTitle}</p>
          <p className="orders-page__empty-hint">{ORDER_UI.emptyHint}</p>
          <Link to={AUTH_PATHS.home} className="orders-page__continue">
            {ORDER_UI.continueShopping}
          </Link>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="orders-page__empty orders-page__no-match">
          <p>{ORDER_UI.noMatchTitle}</p>
          <p className="orders-page__empty-hint">{ORDER_UI.noMatchHint}</p>
          <button
            type="button"
            className="orders-page__clear-btn"
            onClick={handleClearFilters}
          >
            {ORDER_UI.clearFilters}
          </button>
        </div>
      ) : (
        <ul className="order-cards">
          {filteredOrders.map((order) => (
            <li key={order.id}>
              <OrderCard
                order={order}
                cancelling={cancellingId === order.id}
                onCancel={(id) => {
                  void cancel(id)
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
